// Org-side team hooks (+) — the WRAPPER layer (lint-exempt for raw TanStack
// Query). Org-scoped (not band-tenanted): the keys live under ['org', orgId, 'team', …] so they survive a band
// switch (which scorches ['band', …]). is the invite-a-teammate flow + the pending-invites list (copy
// link / revoke); adds the member roster + the artist-assignment gate (assign / unassign) + member
// removal. Every write surfaces the server error verbatim and NEVER reports success on an unpersisted write.
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { components } from "@cntrl/api-client";
import { useApp } from "../appContext.js";
import { ApiError } from "./apiError.js";

type PendingInvitesRead = components["schemas"]["PendingInvitesRead"];
type PendingInvite = components["schemas"]["PendingInvite"];
type InviteIssued = components["schemas"]["InviteIssued"];
type IssueOrgSeatInviteRequest = components["schemas"]["IssueOrgSeatInviteRequest"];
type OrgTeamRead = components["schemas"]["OrgTeamRead"];
type OrgSeatView = components["schemas"]["OrgSeatView"];
type OrgAssignmentView = components["schemas"]["OrgAssignmentView"];
type AssignArtistRequest = components["schemas"]["AssignArtistRequest"];
type AssignmentCreated = components["schemas"]["AssignmentCreated"];

export type {
  PendingInvitesRead,
  PendingInvite,
  InviteIssued,
  IssueOrgSeatInviteRequest,
  OrgTeamRead,
  OrgSeatView,
  OrgAssignmentView,
  AssignArtistRequest,
  AssignmentCreated,
};

/**
 * The assignment `responsibility` vocabulary (the `assign_artist` gate's allowed set,
 * `Modules/Identity/Services/IOrgTeamService.cs` `Responsibilities`). Presentation labels only; the gate
 * validates, and an omitted responsibility is legal (a plain assignment). Not a business value.
 */
export const RESPONSIBILITIES = [
  { value: "responsible_agent", label: "Responsible Agent" },
  { value: "day_to_day", label: "Day-to-Day" },
  { value: "ar_lead", label: "A&R Lead" },
  { value: "publicist", label: "Publicist" },
  { value: "product_manager", label: "Product Manager" },
] as const;

/**
 * Display label for an assignment responsibility key (falls back to the raw key).
 *
 * UNREFERENCED since 2026-09-02. The vocabulary was retired from Team on 2026-09-01 and the roster rail was
 * its last reader, until a born-blank rail row stopped printing provenance in place of its audience figures.
 * Kept only so a historic `responsibility` value still has a renderer if a surface ever needs one; by this
 * repo's own rule (an unread second vocabulary is deleted, not carried) the honest end state is deletion.
 */
export function responsibilityLabel(value: string | null | undefined): string {
  return RESPONSIBILITIES.find((r) => r.value === value)?.label ?? value ?? "";
}

/**
 * `PUT /orgs/{orgId}/team/{seatId}/role` — change a teammate's role.
 *
 * The server re-stamps the seat's four capability flags from the new role's catalog tier, so this is the
 * promote/demote path as well as a job-title change — before it, the flags were stamped once at seat creation
 * with no update path anywhere, and the only way to change someone's role was to remove and re-invite them
 * (losing their artist assignments).
 *
 * Invalidates the roster too: the tier re-stamp can flip `sees_all_bands`, which changes which artists that
 * person reaches, and the rail is drawn from a different key than the team list.
 */
export function useChangeMemberRole(orgId: string) {
  const { api } = useApp();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ seatId, title }: { seatId: string; title: string }) => {
      const { error, response } = await api.PUT("/api/v1/orgs/{orgId}/team/{seatId}/role", {
        params: { path: { orgId, seatId } },
        body: { title },
      });
      if (!response.ok) throw new ApiError(response.status, error);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["org", orgId, "team"] });
      void qc.invalidateQueries({ queryKey: ["org", orgId, "roster"] });
    },
  });
}

/** `GET /orgs/{orgId}/team/invites` — the pending seat invites (copy link / revoke).*/
export function useOrgTeamInvites(orgId: string | undefined) {
  const { api } = useApp();
  return useQuery({
    queryKey: ["org", orgId ?? "none", "team", "invites"],
    enabled: orgId != null && orgId !== "",
    queryFn: async () => {
      const { data, error, response } = await api.GET("/api/v1/orgs/{orgId}/team/invites", {
        params: { path: { orgId: orgId as string } },
      });
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data;
    },
    staleTime: 30_000,
  });
}

/** `POST /orgs/{orgId}/team/invites` ({email, role, title?}) → 201 InviteIssued (share-link always returned). */
export function useInviteTeammate(orgId: string) {
  const { api } = useApp();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: IssueOrgSeatInviteRequest) => {
      const { data, error, response } = await api.POST("/api/v1/orgs/{orgId}/team/invites", {
        params: { path: { orgId } },
        body,
      });
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["org", orgId, "team", "invites"] });
    },
  });
}

/** `DELETE /orgs/{orgId}/team/invites/{inviteId}` → 204 (revoke — kills the token).*/
export function useRevokeInvite(orgId: string) {
  const { api } = useApp();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (inviteId: string) => {
      const { error, response } = await api.DELETE("/api/v1/orgs/{orgId}/team/invites/{inviteId}", {
        params: { path: { orgId, inviteId } },
      });
      if (!response.ok) throw new ApiError(response.status, error);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["org", orgId, "team", "invites"] });
    },
  });
}

// ---- member roster + the assignment gate ---------------------------------------

/**
 * `GET /orgs/{orgId}/team` (`roster:manage_seats`) — the org's active members + their artist assignments
 * + capability flags (Class-D dual read). Keyed `['org', orgId, 'team', 'members']`; a `roster-changed` socket
 * signal invalidates the `['org', orgId, 'team']` prefix (invalidationRegistry). A 403/404 surfaces
 * as the query error state.
 */
export function useOrgTeamMembers(orgId: string | undefined) {
  const { api } = useApp();
  return useQuery({
    queryKey: ["org", orgId ?? "none", "team", "members"],
    enabled: orgId != null && orgId !== "",
    queryFn: async () => {
      const { data, error, response } = await api.GET("/api/v1/orgs/{orgId}/team", {
        params: { path: { orgId: orgId as string } },
      });
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data;
    },
    staleTime: 30_000,
  });
}

/**
 * `POST /orgs/{orgId}/assignments` ({orgSeatId, bandId, responsibility?}) → 201 AssignmentCreated (
 * `roster:manage_links` — `assign_artist`). Requires an active org↔band link; takes effect immediately.
 * Invalidates the `['org', orgId, 'team']` prefix so the member's chips repaint. Server 400/403/409 verbatim.
 */
export function useAssignArtist(orgId: string) {
  const { api } = useApp();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: AssignArtistRequest) => {
      const { data, error, response } = await api.POST("/api/v1/orgs/{orgId}/assignments", {
        params: { path: { orgId } },
        body,
      });
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["org", orgId, "team"] });
    },
  });
}

/** `DELETE /orgs/{orgId}/assignments/{assignmentId}` → 204 (`unassign_artist`). Takes effect immediately.*/
export function useUnassignArtist(orgId: string) {
  const { api } = useApp();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (assignmentId: string) => {
      const { error, response } = await api.DELETE("/api/v1/orgs/{orgId}/assignments/{assignmentId}", {
        params: { path: { orgId, assignmentId } },
      });
      if (!response.ok) throw new ApiError(response.status, error);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["org", orgId, "team"] });
    },
  });
}

/**
 * `DELETE /orgs/{orgId}/team/{seatId}` → 204 (`remove_org_seat`, `roster:manage_seats`). Cannot remove
 * yourself (409 service guard) — the caller's own row hides Remove; the 409 is the defensive backstop.
 * Drops the seat's assignments (gate cascade). Invalidates the member roster.
 */
export function useRemoveOrgMember(orgId: string) {
  const { api } = useApp();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (seatId: string) => {
      const { error, response } = await api.DELETE("/api/v1/orgs/{orgId}/team/{seatId}", {
        params: { path: { orgId, seatId } },
      });
      if (!response.ok) throw new ApiError(response.status, error);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["org", orgId, "team"] });
    },
  });
}
